import type { ReactNode } from "react";
import type { Key } from "react-aria-components";
import { FieldDescription } from "../field/fieldDescription.js";
import { FieldError } from "../field/fieldError.js";
import { Size } from "../helpers/helpers.js";
import { Label } from "../Label/label.js";
import {
  Combobox,
  type ComboboxProps,
  type ComboboxSelectionMode,
} from "./combobox.js";
import { ComboboxChips } from "./comboboxChips.js";
import { ComboboxContent } from "./comboboxContent.js";
import { ComboboxInput } from "./comboboxInput.js";
import { ComboboxItem } from "./comboboxItem.js";

/**
 * Options are keyed by their `id`, which is how react-aria reads a collection
 * item's key — so the field's value is typed as that id, and a branded one
 * (`TeammateId`) survives the round trip.
 */
export interface ComboboxFieldItem {
  id: Key;
}

/** What the field holds. Readonly so a `readonly T[]` in state can be passed straight in. */
export type ComboboxFieldValue<
  T extends ComboboxFieldItem,
  M extends ComboboxSelectionMode,
> = M extends "single" ? T["id"] | null : readonly T["id"][];

/** What a change reports: react-aria builds the next selection fresh either way. */
export type ComboboxFieldChange<
  T extends ComboboxFieldItem,
  M extends ComboboxSelectionMode,
> = M extends "single" ? T["id"] | null : T["id"][];

export interface ComboboxFieldProps<
  T extends ComboboxFieldItem,
  M extends ComboboxSelectionMode = "single",
> extends Omit<
  ComboboxProps<T, M>,
  | "children"
  | "className"
  | "items"
  | "defaultItems"
  | "value"
  | "onChange"
  | "autoFocus"
> {
  /** Every option. Filtered as the user types (see {@link Combobox} on `items`). */
  items: readonly T[];
  /** An option's text — in the menu, in its chip, and to match against. */
  getLabel: (item: T) => string;
  value: ComboboxFieldValue<T, M>;
  onChange: (value: ComboboxFieldChange<T, M>) => void;
  label?: string;
  description?: ReactNode;
  /** Shown in place of `description`, and marks the field invalid. */
  errorMessage?: ReactNode;
  placeholder?: string;
  /** Accessible name for the group of chips. Defaults to `Selected {label}`. */
  chipsLabel?: string;
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
  autoFocus?: boolean;
  className?: string;
  /** As on {@link ComboboxChips}: what to select for pasted values that name no option. */
  onPasteUnmatched?: (values: readonly string[]) => readonly Key[];
}

/**
 * A {@link Combobox} with the field furniture already wired: the label, the
 * chips or text input the selection mode calls for, the option list, and the
 * description or error underneath.
 *
 * Reach for this by default. Options are plain text here — one `getLabel`
 * feeds the menu, the chips and the matching — so compose {@link Combobox} out
 * of its parts instead when an option needs markup of its own, when the
 * options are grouped in {@link ComboboxSection}s, or when the caller filters
 * them server-side. Every {@link Combobox} prop is still accepted and passed
 * through.
 *
 * ```tsx
 * <ComboboxField
 *   label="Teammates"
 *   selectionMode="multiple"
 *   items={teammateOptions}
 *   getLabel={(teammate) => teammate.name}
 *   value={teammateIds}
 *   onChange={setTeammateIds}
 * />
 * ```
 */
export const ComboboxField = <
  T extends ComboboxFieldItem,
  M extends ComboboxSelectionMode = "single",
>(
  props: ComboboxFieldProps<T, M>,
) => {
  const {
    items,
    getLabel,
    value,
    onChange,
    label,
    description,
    errorMessage,
    placeholder,
    chipsLabel,
    size,
    autoFocus,
    className,
    onPasteUnmatched,
    isInvalid,
    ...comboboxProps
  } = props;

  // Anything renderable but empty — `errorMessage={isTooLong && "..."}` while
  // the field is fine — would otherwise mark it invalid and render nothing.
  const hasError = Boolean(errorMessage);
  const isMultiple = props.selectionMode === "multiple";

  return (
    <Combobox<T, M>
      {...comboboxProps}
      // The uncontrolled prop, which is the one the built-in filter reads.
      defaultItems={items}
      // The caller's id type is narrower than the Key react-aria hands around,
      // and a readonly value is wider than the array it wants; both are the
      // same selection either way.
      value={value as ComboboxProps<T, M>["value"]}
      onChange={onChange}
      isInvalid={isInvalid ?? hasError}
      className={className}
    >
      {label ? <Label size={Size.SMALL}>{label}</Label> : null}
      {isMultiple ? (
        <ComboboxChips<T>
          placeholder={placeholder}
          chipsLabel={chipsLabel ?? (label ? `Selected ${label}` : undefined)}
          size={size}
          autoFocus={autoFocus}
          onPasteUnmatched={onPasteUnmatched}
        >
          {getLabel}
        </ComboboxChips>
      ) : (
        <ComboboxInput
          placeholder={placeholder}
          size={size}
          autoFocus={autoFocus}
        />
      )}
      <ComboboxContent<T>>
        {(item) => <ComboboxItem>{getLabel(item)}</ComboboxItem>}
      </ComboboxContent>
      {hasError ? (
        <FieldError>{errorMessage}</FieldError>
      ) : description != null ? (
        <FieldDescription>{description}</FieldDescription>
      ) : null}
    </Combobox>
  );
};
